import apisauce from "apisauce";
import Cache from "../cache/";
import _ from "lodash";

import AppConfig from "../config/AppConfig";
import StoreManager from "../redux/";
import DebugLogger from "../utils/DebugLogger";
import * as Helper from "../utils/Helper";
import { Actions as SessionActions } from "../redux/SessionRedux";
import i18n from "i18next";
import Constants from "../config/Constants";
import ElectronInvoker from "../utils/ElectronInvoker.js";

const LOGOUT_MESSAGE = "Sorry, you’ve been logged out. Please log back in.";

function isMobileWeb() {
    return window.location.pathname.startsWith("/mobile/");
}

export class ApiService {
    constructor() {
        var base64 = require("base-64");
        const hash = base64.encode(AppConfig.APP_CLIENT_ID + ":" + AppConfig.APP_CLIENT_SECRET);
        this.basicAuth = "Basic " + hash;

        // once we get an access token, we will use access token whenever possible
        this.bearerAuth = null;

        let locale = Helper.getLocale(i18n.language);

        this.api = apisauce.create({
            // base URL is read from the "constructor"
            baseURL: AppConfig.APP_SERVICE_BASE,
            // here are some default headers
            headers: {
                "Cache-Control": "no-cache",
                // "Accept-Language": I18n.locale + ", " + I18n.locale.substr(0, 2)
                "Accept-Language": locale + ", " + locale.substr(0, 2),
            },
            // 300 second timeout...
            timeout: 300000,
        });

        this.api.addResponseTransform((response) => {
            response.fromCache = true;
        });

        this.apiWithOffline = apisauce.create({
            // base URL is read from the "constructor"
            baseURL: AppConfig.APP_SERVICE_BASE,
            // here are some default headers
            headers: {
                "Cache-Control": "no-cache",
                // "Accept-Language": I18n.locale + ", " + I18n.locale.substr(0, 2)
                "Accept-Language": locale + ", " + locale.substr(0, 2),
            },
            // 6 second timeout...
            timeout: 6000,
        });

        this.apiWithOffline.addResponseTransform((response) => {
            response.fromCache = true;
        });

        this.replayApiErrorSauce = apisauce.create({
            // base URL is read from the "constructor"
            baseURL: AppConfig.APP_SERVICE_BASE,
            // here are some default headers
            headers: {
                "Cache-Control": "no-cache",
                // "Accept-Language": I18n.locale + ", " + I18n.locale.substr(0, 2)
                "Accept-Language": locale + ", " + locale.substr(0, 2),
            },
            // 300 second timeout...
            timeout: 300000,
        });

        this.keyCache = new Cache(50000);
        this.dataCache = new Cache(50000);
        // define how many ms we wait before we start to check validity of cached data
        this.validityCheckDelay = 1000; // 5 seconds

        // define how many ms we wait before we start to check validity of cached data
        this.validityCheckDelay = 5000; // 5 seconds

        this.tryRefreshTokenTimes = 0;
        this.ignoreConvertingCamelCaseRequestArr = ["v3", "v4", "v5", "v6"]; //ignore converting request data from camelCase to snakeCase
    }

    setBaseURL(baseURL) {
        this.api.setBaseURL(baseURL);
        this.apiWithOffline.setBaseURL(baseURL);
        this.replayApiErrorSauce.setBaseURL(baseURL);
    }

    forceGet(apiUrl, jsonData, successCallback, failureCallback) {
        this._ajax("get", apiUrl, jsonData, true, successCallback, failureCallback);
    }

    get(apiUrl, jsonData, successCallback, failureCallback) {
        this._ajax("get", apiUrl, jsonData, false, successCallback, failureCallback);
    }

    forcePost(apiUrl, jsonData, successCallback, failureCallback) {
        this._ajax("post", apiUrl, jsonData, true, successCallback, failureCallback);
    }

    post(apiUrl, jsonData, successCallback, failureCallback) {
        this._ajax("post", apiUrl, jsonData, false, successCallback, failureCallback);
    }

    _ajax(method, apiUrl, jsonData, refreshForced, successCallback, failureCallback) {
        DebugLogger.log("Ajax: " + apiUrl + " is called.");

        //let base = this;
        // update bearer authorization header
        this.bearerAuth = this._getBearerAuthorizationHeader();

        // set request header for authentication
        this.api.setHeader("Authorization", this.bearerAuth ? this.bearerAuth : this.basicAuth);
        this.api.setHeader("Content-Type", "application/json");

        let locale = Helper.getLocale(i18n.language);
        this.api.setHeader("Accept-Language", locale + ", " + locale.substr(0, 2));
        this.api.setBaseURL(AppConfig.APP_SERVICE_BASE);
        // set a empty object for null or undefined jsonData
        if (!jsonData) {
            jsonData = {};
        }

        //clone jsonData to avoid mutating the original jsonData
        jsonData = _.cloneDeep(jsonData);

        // add a clientType so that service knows where the call is coming from
        // just in case we may need some special logic to handle requests from different
        // clients
        // clientType: 0 - web, 1 - app
        jsonData.clientVersion = AppConfig.APP_CLIENT_VERSION;
        jsonData.clientBuild = isMobileWeb() ? AppConfig.MOBILE_APP_CLIENT_BUILD : AppConfig.APP_CLIENT_BUILD;
        jsonData.clientType = 0;
        jsonData.timeZone = Helper.getTimeZone();
        jsonData.clientOS = "Web";

        // //we also need to convert request data from camelCase to snakeCase
        // //do not convert after v3
        // if (!_.some(this.ignoreConvertingCamelCaseRequestArr, (el) => _.includes(_.toLower(apiUrl), _.toLower(el)))) {
        //     jsonData = Helper.convertCamelCaseKeysToSnakeCase(jsonData);
        // }

        // build json string based on json data
        const jsonDataString = JSON.stringify(jsonData);
        // create a unique post identifier based on serivce url and json data
        const uid = apiUrl + "@" + jsonDataString;
        DebugLogger.log("_ajax is called. {apiUrl: " + apiUrl + ", jsonData: " + jsonDataString + "}");

        // indicate if we need to get data from server
        if (!refreshForced) {
            // find the cached key based on the unique identifier of the request
            const cachedKey = this.keyCache.get(uid);
            // append cachedKey as a request parameter and get value based on key
            let value = null;
            if (cachedKey) {
                jsonData.cachedKey = cachedKey;
                value = this.dataCache.get(cachedKey);
            } else {
                jsonData.cachedKey = null;
            }
            // create a unique post identifier based on serivce url and json data
            if (value) {
                if (successCallback) successCallback(value);
                setTimeout(() => {
                    this._callAjax(
                        method,
                        apiUrl,
                        jsonData,
                        (response, pCacheKey) => {
                            let tCacheKey = pCacheKey;
                            if (!tCacheKey && response) {
                                tCacheKey = response.cachedKey;
                            }
                            // if any new data, then call success callback
                            // otherwise, do nothing
                            if (cachedKey !== tCacheKey) {
                                DebugLogger.log("Cached data is updated by others. {uid: " + uid + "}");

                                // remove old cached data since we don't need it any more.
                                this.dataCache.remove(cachedKey);

                                DebugLogger.log("Old cached data is removed from the cache. {uid: " + uid + "}");

                                if (tCacheKey !== null && tCacheKey !== "") {
                                    // update the cache key to the key cache
                                    this.keyCache.set(uid, tCacheKey);

                                    // update the data to the data cache
                                    this.dataCache.set(tCacheKey, response);

                                    DebugLogger.log("Cached data is updated for this service call. {uid: " + uid + ", cachedKey:" + tCacheKey + "}");
                                }

                                // call sucdess callback for newly updated data
                                if (successCallback) successCallback(response);
                            } else {
                                DebugLogger.log("Cached data is up-to-date. {uid: " + uid + "}");
                            }
                        },
                        failureCallback,
                        0
                    );
                }, this.validityCheckDelay);

                return;
            }
        }

        this._callAjax(
            method,
            apiUrl,
            jsonData,
            (response, cacheKey) => {
                let tCacheKey = cacheKey;
                if (!tCacheKey && response) {
                    tCacheKey = response.cachedKey;
                }
                if (tCacheKey) {
                    // add new cached key to the key cache
                    this.keyCache.set(uid, tCacheKey);

                    // add new data to the data cache
                    this.dataCache.set(tCacheKey, response);

                    DebugLogger.log("Data is added to the cache for this service call. {uid: " + uid + ", cachedKey:" + tCacheKey + ", cache size: " + this.dataCache.cache.size + "}");
                }

                if (successCallback) successCallback(response);
            },
            failureCallback,
            0
        );
    }

    _callAjax(method, apiUrl, jsonData, successCallback, failureCallback, retriedTimes) {
        let base = this;
        // for login related api, we don't use ipc proxy
        if (!this._isLoginRelatedCall(apiUrl)) {
            // Try IPC-based proxy first in Electron Desktop
            try {
                const locale = Helper.getLocale(i18n.language);
                const headers = {
                    Authorization: this.bearerAuth ? this.bearerAuth : this.basicAuth,
                    "Accept-Language": locale + ", " + locale.substr(0, 2),
                    "Content-Type": "application/json",
                    "Cache-Control": "no-cache",
                };

                const payload = JSON.stringify({
                    method: (method || "GET").toUpperCase(),
                    endpoint: apiUrl,
                    body: jsonData,
                    headers,
                    options: { timeoutMs: 300000 }
                });

                ElectronInvoker
                    .invoke("apiproxy:request", payload)
                    .then((ipcResponseraw) => {
                        let ipcResponse = JSON.parse(ipcResponseraw);
                        ipcResponse = Helper.convertSnakeCaseKeysToCamelCase(ipcResponse);
                        console.log("IPC request response", ipcResponse);

                        // Check if response indicates success (aligned with apisauce response structure)
                        if (ipcResponse.ok === false) {
                            // Handle HTTP error responses (e.g., 500, 403, etc.)
                            const status = ipcResponse.status;
                            const problem = ipcResponse.problem;
                            const errorData = ipcResponse.data;

                            if (status === 403) {
                                // Token expired - try refresh token
                                let store = StoreManager.getStore();
                                let authorization = store.getState().session.authorization;
                                if (authorization && authorization.refreshToken) {
                                    DebugLogger.log("IPC Response status 403. Try Refresh Token", authorization);
                                    base.api.setHeaders({
                                        Authorization: base.basicAuth,
                                        "Content-Type": "application/x-www-form-urlencoded",
                                    });
                                    base.api["post"]("/OAuth/Token.ashx", `grant_type=refresh_token&scope=iml.user&refresh_token=${authorization.refreshToken}`)
                                        .then((refreshResponse) => {
                                            if (refreshResponse.ok && refreshResponse.data) {
                                                DebugLogger.groupMessage("Refresh Token Done", refreshResponse);
                                                refreshResponse = Helper.convertSnakeCaseKeysToCamelCase(refreshResponse);
                                                store.dispatch(SessionActions.receiveRefreshedToken(refreshResponse.data));
                                                DebugLogger.log("ReSend Previous Request");
                                                base._ajax(method, apiUrl, jsonData, true, successCallback, failureCallback);
                                            } else {
                                                store.dispatch(SessionActions.logout());
                                                DebugLogger.groupMessage("Refresh Token Failed", refreshResponse);
                                                if (failureCallback) failureCallback(base._formalizeError(LOGOUT_MESSAGE, problem));
                                            }
                                        })
                                        .catch((e) => {
                                            store.dispatch(SessionActions.logout());
                                            if (failureCallback) failureCallback(base._formalizeError(LOGOUT_MESSAGE, e.message));
                                        });
                                } else {
                                    store.dispatch(SessionActions.logout());
                                    if (failureCallback) failureCallback(this._formalizeError(LOGOUT_MESSAGE, problem));
                                }
                            } else {
                                // Other HTTP errors (500, 400, etc.)
                                if (failureCallback) failureCallback(this._formalizeError(errorData, problem));
                            }
                            return;
                        }

                        const returnedData = ipcResponse.data;
                        // Align with existing callback semantics
                        if (returnedData && _.has(returnedData, "isDone")) {
                            if (returnedData.isDone) {
                                // Check for deactivated account
                                if (returnedData.data && returnedData.data.isDeactivatedAccount) {
                                    let store = StoreManager.getStore();
                                    store.dispatch(SessionActions.logout());
                                }
                                if (successCallback) successCallback(returnedData.data, returnedData.cachedKey);
                            } else {
                                if (returnedData.code === Constants.CODES.NOT_EXIST_CURUSER) {
                                    StoreManager.getStore().dispatch(SessionActions.logout());
                                } else if (failureCallback) {
                                    failureCallback(this._formalizeError(returnedData));
                                }
                            }
                        } else {
                            if (successCallback) successCallback(returnedData);
                        }
                    })
                    .catch((e) => {
                        console.log("IPC request failed");
                        console.error(e);
                        if (failureCallback) failureCallback(this._formalizeError(null, e && e.message ? e.message : "IPC request failed"));
                    });
                return;
            } catch (e) {
                // Fallback to HTTP below
                console.log("IPC request failed");
                console.error(e);
            }
            return;
        }

        // Fallback: call api directly to return data
        this.api[method](apiUrl, jsonData)
            .then((response) => {
                // response = {};
                // response.li_text_str = "hello world.";
                // response.li_users = ["mike", "john", "frank"];
                // response.xi_list = new Array();
                // response.xi_list[0] = { name_first: "wake", age_int: 12 };
                // response.xi_list[1] = { id_number: 1910901, status_text: "freshman" };

                // response = Helper.convertSnakeCaseKeysToCamelCase(response);

                // convert snakeCase to camelCase
                response = Helper.convertSnakeCaseKeysToCamelCase(response);

                if (response.ok) {
                    // this.dataCache.set(apiKey, response.data);

                    var returnedData = response.data;
                    if (_.has(returnedData, "isDone")) {
                        // if returned data is ResultOutVO
                        if (returnedData.isDone) {
                            //put cachedkey as 2nd param since here we return the data of ApiResponse
                            //AIC-9-354 如果是冻结的老师或代理账号则直接退出登录
                            if (returnedData.data && returnedData.data.isDeactivatedAccount) {
                                let store = StoreManager.getStore();
                                store.dispatch(SessionActions.logout());
                            }
                            if (successCallback) successCallback(returnedData.data, returnedData.cachedKey);
                        } else {
                            if (returnedData.code === Constants.CODES.NOT_EXIST_CURUSER)
                                //current user is not exist
                                StoreManager.getStore().dispatch(SessionActions.logout());
                            else if (failureCallback) failureCallback(this._formalizeError(returnedData));
                        }
                    } else {
                        //other case no need
                        if (successCallback) successCallback(response.data);
                    }
                } else {
                    if (response.problem && (response.problem === apisauce.NETWORK_ERROR || response.problem === apisauce.CONNECTION_ERROR || response.problem === apisauce.TIMEOUT_ERROR)) {
                        if (retriedTimes < 1) {
                            setTimeout(() => {
                                retriedTimes = retriedTimes + 1;
                                this._callAjax(method, apiUrl, jsonData, successCallback, failureCallback, retriedTimes);
                            }, 3000);
                        } else {
                            if (failureCallback) failureCallback(this._formalizeError(response.data, response.problem, apiUrl, jsonData));
                        }
                    } else if (response.status === 403 /* forbidden */) {
                        // probably access token is expired, we need to use refresh token to get a new
                        // access token so that user can continue access the app without any issue

                        // try to refresh token here
                        let store = StoreManager.getStore();
                        let authorization = store.getState().session.authorization;
                        //if we already has tried 3 times here, we just assume it as that we can not refresh token
                        if (authorization && authorization.refreshToken) {
                            DebugLogger.log("Response status 403.Try Refresh Token", authorization);
                            //set specific headers for refresh token
                            base.api.setHeaders({
                                Authorization: base.basicAuth,
                                "Content-Type": "application/x-www-form-urlencoded",
                            });

                            base.api["post"]("/OAuth/Token.ashx", `grant_type=refresh_token&scope=iml.user&refresh_token=${authorization.refreshToken}`)
                                .then((refreshResponse) => {
                                    if (refreshResponse.ok && refreshResponse.data) {
                                        DebugLogger.groupMessage("Refresh Token Done", refreshResponse);
                                        refreshResponse = Helper.convertSnakeCaseKeysToCamelCase(refreshResponse);
                                        //dispatch action to set and save in session
                                        store.dispatch(SessionActions.receiveRefreshedToken(refreshResponse.data));
                                        //re send previous request
                                        DebugLogger.log("ReSend Previous Request");

                                        base._ajax(method, apiUrl, jsonData, true, successCallback, failureCallback);
                                    } else {
                                        store.dispatch(SessionActions.logout());

                                        DebugLogger.groupMessage("Refresh Token Failed", refreshResponse);
                                        if (failureCallback) failureCallback(this._formalizeError(LOGOUT_MESSAGE, response.problem));
                                    }
                                })
                                .catch((e) => {
                                    store.dispatch(SessionActions.logout());
                                    if (failureCallback) failureCallback(this._formalizeError(LOGOUT_MESSAGE, e.message));
                                });
                        } else {
                            // if (this.tryRefreshTokenTimes >= 3) {
                            //     // we need to clear the session data and ask user to log in again to get token in this case
                            //     // so that we can avoid some issues with token refreshing.
                            //     store.dispatch(SessionActions.logout());
                            // }
                            store.dispatch(SessionActions.logout());
                            if (failureCallback) failureCallback(this._formalizeError(LOGOUT_MESSAGE, response.problem));
                        }
                    } else {
                        if (failureCallback) failureCallback(this._formalizeError(response.data, response.problem));
                    }
                }
            })
            .catch((e) => {
                if (failureCallback) failureCallback(this._formalizeError(null, e.message));
            });
    }

    _isLoginRelatedCall(url){
        const relatedApiArray = ["/admin/account/"];

        return _.some(relatedApiArray, (api) => url.startsWith(api));
    }
    _getBearerAuthorizationHeader() {
        var store = StoreManager.getStore();
        var authorization = store.getState().session.authorization;
        if (authorization && authorization.accessToken) {
            return "Bearer " + authorization.accessToken;
        }

        return null;
    }


    postFileToServer(formData, width, height, successCallback, failureCallback) {
        var apiUrl = AppConfig.FILE_SERVER+"/Upload.ashx?";
        if (width > 0 && height > 0)
            apiUrl += `w=${width}&h=${height}`;

        // update bearer authorization header
        this.bearerAuth = this._getBearerAuthorizationHeader();

        // set request header for authentication

        fetch(apiUrl, {
            method: "POST",
            headers: {
                "Cache-Control": "no-cache",
                // "Accept-Language": I18n.locale + ", " + I18n.locale.substr(0, 2),
                "Accept-Language": "en-US, en",
                "Authorization": this.bearerAuth ? this.bearerAuth : this.basicAuth
            },
            body: formData,
        }).then((response) => response.json())
            .then((responseData) => {
                var newResponse = { data: responseData.Data, message: responseData.Message };
                if (responseData.IsDone) {
                    if (successCallback) successCallback(newResponse);
                } else {
                    DebugLogger.log("_postFileApi err", responseData.Message);
                    if (failureCallback) failureCallback(newResponse);
                }
            })
            .catch((err) => {
                DebugLogger.log("_postFileApi err", err);
                if (failureCallback) failureCallback(err);
            });
    }



    _formalizeError(data, problem) {
        var message = "Unknow error.";
        if (problem && problem !== "") {
            message = problem;
        }

        if (!data) {
            return { code: 1, message: message };
        }

        if (_.isString(data)) {
            return { code: 1, message: data };
        }

        var error = { ...data };

        if (!_.has(error, "code")) {
            error.code = 1; // Unknow
        }

        const nestedMessage = _.get(error, "data.message") || _.get(error, "data.data.message") || _.get(error, "error.message");
        if (!_.has(error, "message") || error.message === null || error.message === "") {
            error.message = nestedMessage || message;
        }

        return error;
    }
}


